import { cssToken } from "@certn/cascadia/utils";
import { CascadiaColor, CascadiaMiniIcon } from "@certn/cascadia-tokens";
import { css, cx } from "@emotion/css";
import { AriaRole } from "react";
import { Button, ButtonProps } from "react-aria-components";

import { Icon } from "../../foundations/icons/Icon/Icon";
import { Text } from "../../foundations/typography/Text";
import { NeutralBadge } from "../badge/NeutralBadge";

interface Props extends ButtonProps {
  avatar?: React.ReactNode;
  background?: CascadiaColor;
  badge?: string;
  customBadge?: React.ReactNode;
  children: string;
  hoverFill?: CascadiaColor;
  icon?: CascadiaMiniIcon;
  rightIcon?: CascadiaMiniIcon;
  iconColor?: CascadiaColor;
  isDropdown?: boolean;
  role?: AriaRole;
  showBorder?: boolean;
  testID: string;
  textColor?: CascadiaColor;
  borderColor?: CascadiaColor;
  maxWidth?: boolean;
}

export function _Chip(props: Props) {
  const {
    avatar,
    background,
    badge,
    children,
    hoverFill,
    icon,
    iconColor = "icon-weak",
    isDropdown,
    borderColor,
    showBorder = borderColor !== undefined,
    testID,
    textColor = "text-base",
    customBadge,
    isDisabled,
    rightIcon,
    maxWidth,
    ...buttonProps
  } = props;
  const styles = getStyles(maxWidth);

  return (
    <Button
      data-testid={testID}
      className={cx(
        styles.chip,
        background && css({ backgroundColor: cssToken(background) }),
        hoverFill &&
          css({
            "&[data-hovered]": css({ backgroundColor: cssToken(hoverFill) }),
          }),
        icon !== undefined && styles.iconPadding,
        avatar !== undefined && styles.avatarPadding,
        isDropdown && styles.dropdownPadding,
        showBorder &&
          css({
            boxShadow: `inset 0px 0px 0px 1px ${cssToken(
              borderColor || "border-base"
            )}`,
          }),
        isDisabled &&
          css({
            backgroundColor: "var(--background-input-disabled)",
          })
      )}
      isDisabled={isDisabled}
      {...buttonProps}
    >
      {avatar}
      {icon && <Icon type="mini" name={icon} color={iconColor} />}
      <Text
        style="input/text-chip"
        color={isDisabled ? "text-weak" : textColor}
        truncate
      >
        {children}
      </Text>
      {badge && <NeutralBadge>{badge}</NeutralBadge>}
      {customBadge && customBadge}
      {isDropdown && (
        <Icon type="standard" name="expand_more" color={iconColor} />
      )}
      {rightIcon && <Icon type="mini" name={rightIcon} color={iconColor} />}
    </Button>
  );
}

function getStyles(maxWidth?: boolean) {
  return {
    chip: css({
      outline: "none",
      alignItems: "center",
      background: "transparent",
      borderRadius: 300,
      borderStyle: "solid",
      borderWidth: 0,
      cursor: "pointer",
      display: "flex",
      flexDirection: "row",
      gap: "var(--space-1)",
      justifyContent: "center",
      minHeight: 32,
      minWidth: "var(--space-12)",
      paddingLeft: "var(--space-3)",
      paddingRight: "var(--space-3)",
      backgroundColor: "var(--background-base)",
      maxWidth: maxWidth ? "200px" : "none",
      "&[data-focus-visible]": { boxShadow: "var(--focus-action)" },
      "&[data-hovered]": {
        backgroundColor: "var(--background-ghost-hover)",
      },
    }),
    border: css({
      boxShadow: `inset 0px 0px 0px 1px var(--border-base)`,
    }),
    dropdownPadding: css({ paddingRight: "var(--space-2-5)" }),
    avatarPadding: css({
      paddingLeft: "var(--space-1)",
      paddingTop: 0,
      paddingBottom: 0,
    }),
    iconPadding: css({ paddingLeft: "var(--space-2-5)" }),
  };
}
